import { getSubdomainFranchise } from '../utils/get-subdomain';
import { httpInterceptor } from './http.interceptor';

export interface RankingUser {
  user: {
    id: number;
    nome_razao_social: string;
    codigo: string;
  };
  total: number;
  quantidadeVendas: number;
}

export interface RankingResponse {
  rankingValue: RankingUser[];
  rankingQntd: RankingUser[];
}

class SaleService {
  private subdomain: string;

  constructor() {
    this.subdomain = getSubdomainFranchise();
  }

  async getAllSalesByUserId(id: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/get-all-sales-by-user-id/${id}`);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getSaleWithCodePagarme(code: string): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/get-sale-code-pagarme/${code}`);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getSaleDetail(idpagarme: string): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/get-sale-detail/${idpagarme}`);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async registerPixSale(saleData: any, codigo_revendedora: number): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/register-pix', { 
        ...saleData, 
        codigo_revendedora 
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async createPixSale(data: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/pix', data);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async createCardSale(data: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale', data);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async createCheckoutLink(data: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/checkout', data);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getEstatisticasMensais(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/estatisticas-mensais/${userId}`);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-api-key': btoa(this.subdomain),
    };
  }

  async getRanking(period: string, type: string, periodoInicio: Date | null = null, periodoFim: Date | null = null): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/get-ranking', {
        isWeek: period === 'week',
        date_start: periodoInicio,
        date_end: periodoFim
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getRankingSalesHome(dataInicio: any, dataFim: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/get-ranking-sales-home', {
        data_inicio: dataInicio,
        data_fim: dataFim
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getGraphSalesMonthlyUser(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/graph-data/sales-monthly-user/${userId}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getTotalSalesThisMonthUser(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/sales-this-month-user/${userId}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Cria uma nova garantia
   * @param garantia Dados da garantia a ser criada
   * @returns Resultado da criação da garantia
   */
  async createGarantia(garantia: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/create-warranty', garantia, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Faz upload da imagem da garantia
   * @param idwarranty ID da garantia
   * @param image Imagem em base64
   * @param fileName Nome do arquivo
   * @returns Resultado do upload com URL de download
   */
  async uploadWarrantyImage(idwarranty: number, image: string, fileName: string): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/upload-warranty-image', {
        idwarranty,
        image,
        fileName
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Busca todas as garantias
   * @param id ID do usuário (opcional, para filtrar por usuário)
   * @returns Lista de garantias
   */
  async getAllWarranty(id?: number): Promise<any> {
    try {
      let url = '/sale/all-warranty';
      if (id) {
        url = `${url}/${id}`;
      }
      const response = await httpInterceptor.get(url, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Busca garantias por dados específicos
   * @param tipo Tipo de busca (codigo, cliente, cpf)
   * @param data Dados para busca
   * @param id ID do usuário (opcional)
   * @returns Garantias encontradas
   */
  async searchWarrantyByData(tipo: string, data: string, id?: number): Promise<any> {
    try {
      let url = '/sale/search-warranty-by-data';
      if (id) {
        url = `${url}/${id}`;
      }
      const response = await httpInterceptor.post(url, {
        tipo,
        data
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getAllWarrantyPaginated(params: {
    page: number;
    limit: number;
    search?: string;
    tipo?: string;
    dateStart?: string;
    dateEnd?: string;
    userId?: number;
  }): Promise<{ data: any[]; total: number; page: number; limit: number }> {
    try {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit));
      if (params.search) query.set('search', params.search);
      if (params.tipo) query.set('tipo', params.tipo);
      if (params.dateStart) query.set('dateStart', params.dateStart);
      if (params.dateEnd) query.set('dateEnd', params.dateEnd);
      if (params.userId) query.set('userId', String(params.userId));

      const response = await httpInterceptor.get(`/sale/all-warranty-paginated?${query.toString()}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Atualiza a situação/etapa de uma garantia
   * @param id ID da garantia
   * @param situation Nova situação
   * @returns Resultado da atualização
   */
  async updateWarrantySituation(id: number, situation: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/sale/update-warranty-situation/${id}`, {
        situation
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Busca todas as vendas com dados do usuário (admin)
   */
  async findAllSalesWithUserData(): Promise<any> {
    try {
      const response = await httpInterceptor.get('/sale/get-all-sales-with-user-data');
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Busca vendas paginadas com dados do usuário (admin)
   */
  async findAllSalesWithUserDataPaginated(params: {
    page: number;
    limit: number;
    search?: string;
    dateStart?: string;
    dateEnd?: string;
    situation?: string;
    clientFilter?: string;
  }): Promise<{ data: any[]; total: number; page: number; limit: number }> {
    try {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit));
      if (params.search) query.set('search', params.search);
      if (params.dateStart) query.set('dateStart', params.dateStart);
      if (params.dateEnd) query.set('dateEnd', params.dateEnd);
      if (params.situation) query.set('situation', params.situation);
      if (params.clientFilter) query.set('clientFilter', params.clientFilter);

      const response = await httpInterceptor.get(`/sale/get-all-sales-with-user-data-paginated?${query.toString()}`);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Atualiza a situação/acerto de uma venda
   */
  async updateSaleSituation(id: number, situation: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/sale/update-sale-situation/${id}`, {
        situation
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Atualiza a situação de múltiplas vendas em massa
   */
  async updateSaleSituationBulk(ids: number[], situation: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/sale/update-sale-situation/bulk', {
        ids,
        situation,
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Cria checkout para pagamento de kit/maleta do pré-cadastro
   */
  async createCheckoutKitMaleta(data: {
    kitId: number;
    kitName: string;
    kitPrice: number;
    installments: number;
    codigo_revendedora: string;
    nome_razao_social: string;
    id_revendedora: number;
    pre_register_id: number;
    pre_register_attachment_id: number;
  }): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/checkout-kit-maleta', data);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Verifica se o pagamento do kit foi realizado
   */
  async checkKitPayed(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/sale/check-kit-payed/${attachmentId}`);
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  /**
   * Exclui uma garantia
   * @param garantia Dados da garantia a ser excluída
   * @returns Resultado da exclusão
   */
  async deleteGarantia(garantia: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/sale/delete-warranty', garantia.sale, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }
}

export const saleService = new SaleService();
